import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import {
  FormLabel,
  MenuItem,
  Select as MuiSelect,
  SelectProps,
  SxProps,
} from "@mui/material";
import { ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";
import { FormContext } from "../../../context";
import { palette } from "../../../styles/theme";
import { getErrorMessage } from "../../../utils/FormValidationsUtils";
import { Box } from "../Box";
import { Label } from "../Typography";
import { AdminIcon } from "../Icon";
import { useAuth } from "../../hooks/useAuth";
import { logSpecsModelPath } from "../../../containers/SpecsEditor/specsModelPathLogger";

interface Props extends Omit<SelectProps, "error"> {
  label?: string;
  link?: ReactNode;
  error?: string;
  name?: SelectProps["name"];
  containerSx?: SxProps;
  noMaxWidth?: boolean;
  controlled?: boolean;
  adminOnly?: boolean;
  "data-test"?: string;
  readonly?: boolean;
  debugPath?: string;
  options?: {
    label: string;
    value: string;
    disabled?: boolean;
    render?: (option: { label: string; value: string }) => ReactNode;
  }[];
}

export const Select = ({
  label,
  link,
  name = "",
  containerSx,
  noMaxWidth,
  onChange,
  error,
  controlled,
  options,
  adminOnly,
  readonly,
  debugPath,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { formik } = useContext(FormContext);
  const isTouched = formik?.touched[name];

  if (adminOnly && !isAdmin) return null;

  return (
    <Box
      width="100%"
      maxWidth={noMaxWidth ? undefined : "400px"}
      display="flex"
      justifyItems="center"
      flexDirection="column"
      minWidth={props.fullWidth ? "100%" : undefined}
      sx={{
        px: "2px",
        ...containerSx,
      }}
    >
      {link ? (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          {!!label && (
            <Label
              htmlFor={props.id}
              onClick={
                debugPath ? () => logSpecsModelPath(debugPath) : undefined
              }
            >
              {label}
            </Label>
          )}
          {link}
        </Box>
      ) : (
        !!label && (
          <Box display={"flex"} gap={"4px"} alignItems={"center"} mb={0.5}>
            <Label
              htmlFor={props.id}
              onClick={
                debugPath ? () => logSpecsModelPath(debugPath) : undefined
              }
              sx={{ textAlign: "start" }}
            >
              {label}
            </Label>
            {adminOnly && <AdminIcon />}
          </Box>
        )
      )}

      <MuiSelect
        {...props}
        className={readonly ? "readonly" : ""}
        IconComponent={KeyboardArrowDown}
        onChange={
          formik && !controlled
            ? (e) => formik?.setFieldValue(name, e.target.value)
            : onChange
        }
        onClose={() => {
          setTimeout(() => {
            (document.activeElement as any)?.blur();
          }, 0);
        }}
      >
        {options
          ? options?.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                selected={option.value === props.value}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: palette.brand.paleEffect,
                  },
                }}
              >
                {option.render ? option.render(option) : option.label}
              </MenuItem>
            ))
          : props.children}
      </MuiSelect>
      {!!formik?.errors[name] && !!isTouched && (
        <FormLabel sx={{ textAlign: "start" }} color="error" error>
          {getErrorMessage(t, formik?.errors[name])}
        </FormLabel>
      )}
      {!!error && (
        <FormLabel sx={{ textAlign: "start" }} color="error" error>
          {getErrorMessage(t, error)}
        </FormLabel>
      )}
    </Box>
  );
};
